// src/pages/customerComponents/SignupPage.js
import React, { useState, useEffect, useRef } from 'react';
import '../../styles/CustomerSiteStyles/SignupPage.css';

// ─── Config ────────────────────────────────────────────────────────────────
// TODO: Replace with the real Zapier webhook URL for inspection signups
const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/21939092/4of2i5a/';

const submitToZapier = async (formData, amName) => {
  const nameParts = formData.full_name.trim().split(' ');
  const payload = {
    first_name:        nameParts[0] || '',
    last_name:         nameParts.slice(1).join(' ') || '',
    email:             formData.email,
    phone:             formData.phone,
    address_line1:     formData.address_line1,
    address_line2:     formData.address_line2,
    city:              formData.city,
    state:             formData.state,
    zip:               formData.zip,
    insurance_company: formData.insurance_company,
    message:           formData.message,
    am_name:           amName || '',
    job_name:          `${formData.full_name} - Inspection Request`,
    job_type:          'Inspection',
    job_status:        'New Lead',
  };

  const res = await fetch(ZAPIER_WEBHOOK_URL, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Zapier webhook failed');
};

// ─── AM Name formatter ──────────────────────────────────────────────────────
// Converts camelCase ref param to a display name, e.g. "LayneMcIntosh" → "Layne McIntosh"
const formatAMName = (raw) =>
  raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')      // split camelCase words
    .replace(/\b(Mc|Mac)\s([A-Z])/g, '$1$2'); // rejoin Mc/Mac prefixes

// ─── Fade-in hook ───────────────────────────────────────────────────────────
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Validation ─────────────────────────────────────────────────────────────
const validate = (name, value) => {
  const v = (value || '').trim();
  switch (name) {
    case 'full_name':
      return v ? '' : 'Name is required.';
    case 'email':
      if (!v) return 'Email is required.';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Enter a valid email.';
    case 'phone':
      if (!v) return 'Phone is required.';
      return /^\d{10}$/.test(v) ? '' : 'Must be exactly 10 digits.';
    case 'address_line1':
      return v ? '' : 'Address is required.';
    case 'city':
      return v ? '' : 'City is required.';
    case 'state':
      return v ? '' : 'State is required.';
    case 'zip':
      if (!v) return 'ZIP is required.';
      return /^\d{5}$/.test(v) ? '' : 'Must be exactly 5 digits.';
    default:
      return '';
  }
};

const REQUIRED_FIELDS = ['full_name', 'email', 'phone', 'address_line1', 'city', 'state', 'zip'];

// ─── Field wrapper ───────────────────────────────────────────────────────────
const Field = ({ id, label, required, error, className = '', children }) => (
  <div className={`sp-form-group ${className}`}>
    <label htmlFor={id}>
      {label}{required && <span className="sp-required"> *</span>}
    </label>
    <div className="sp-input-wrap">
      {error && (
        <div className="sp-tooltip" role="alert">
          {error}
          <span className="sp-tooltip__arrow" />
        </div>
      )}
      {children}
    </div>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const SignupPage = () => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    insurance_company: '', message: '',
  });

  const [touched, setTouched]   = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus]     = useState('idle'); // idle | sending | success | error
  const [heroRef, heroVisible]  = useFadeIn(0.05);

  // Capture ?ref= query param on load and persist to sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && ref.trim()) {
      sessionStorage.setItem('AM Name', formatAMName(ref.trim()));
    }
  }, []);

  const getError = (name) => {
    if (!submitted && !touched[name]) return '';
    return validate(name, form[name]);
  };

  const handleChange = (e) => {
    const { name, value: raw } = e.target;
    const value =
      name === 'phone' ? raw.replace(/\D/g, '').slice(0, 10) :
      name === 'zip'   ? raw.replace(/\D/g, '').slice(0, 5)  :
      name === 'state' ? raw.slice(0, 2).toUpperCase()        :
      raw;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const hasErrors = REQUIRED_FIELDS.some((f) => validate(f, form[f]));
    if (hasErrors) return;

    setStatus('sending');
    try {
      const amName = sessionStorage.getItem('AM Name') || '';
      await submitToZapier(form, amName);
      setStatus('success');
      setForm({
        full_name: '', email: '', phone: '',
        address_line1: '', address_line2: '', city: '', state: '', zip: '',
        insurance_company: '', message: '',
      });
      setTouched({});
      setSubmitted(false);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="sp-page">


      {/* ── Signup Form ── */}
      <section className="sp-form-section">
        <div className="sp-section-header">
          <div className="sp-tag">Get Started</div>
          <h2 className="sp-section-title">Request Your Inspection</h2>
          <p className="sp-section-sub">
            Fill out the form below and we'll reach out within one business day to schedule your free roof inspection.
          </p>
        </div>

        <div className="sp-form-card">
          {status === 'success' ? (
            <div className="sp-form-success">
              <div className="sp-form-success__icon">✅</div>
              <h3>Request Received!</h3>
              <p>
                Thanks for reaching out! We've received your inspection request and a member of
                our team will contact you within one business day to confirm your appointment.
              </p>
            </div>
          ) : (
            <form className="sp-form" onSubmit={handleSubmit} noValidate>

              <div className="sp-form-row">
                <Field id="full_name" label="Full Name" required error={getError('full_name')}>
                  <input
                    id="full_name" name="full_name" type="text" placeholder="Jane Smith"
                    value={form.full_name} onChange={handleChange} onBlur={handleBlur}
                  />
                </Field>
                <Field id="email" label="Email Address" required error={getError('email')}>
                  <input
                    id="email" name="email" type="email" placeholder="jane@example.com"
                    value={form.email} onChange={handleChange} onBlur={handleBlur}
                  />
                </Field>
              </div>

              <div className="sp-form-row">
                <Field id="phone" label="Phone Number" required error={getError('phone')}>
                  <input
                    id="phone" name="phone" type="tel" placeholder="5550001234"
                    value={form.phone} onChange={handleChange} onBlur={handleBlur}
                    maxLength={10}
                  />
                </Field>
                <Field id="address_line1" label="Address Line 1" required error={getError('address_line1')}>
                  <input
                    id="address_line1" name="address_line1" type="text" placeholder="123 Main St"
                    value={form.address_line1} onChange={handleChange} onBlur={handleBlur}
                  />
                </Field>
              </div>

              <div className="sp-form-row">
                <Field id="address_line2" label="Address Line 2" error="" className="sp-form-group--full">
                  <input
                    id="address_line2" name="address_line2" type="text"
                    placeholder="Apt, Suite, Unit (optional)"
                    value={form.address_line2} onChange={handleChange}
                  />
                </Field>
              </div>

              <div className="sp-form-row--3">
                <Field id="city" label="City" required error={getError('city')}>
                  <input
                    id="city" name="city" type="text" placeholder="Chicago"
                    value={form.city} onChange={handleChange} onBlur={handleBlur}
                  />
                </Field>
                <Field id="state" label="State" required error={getError('state')}>
                  <input
                    id="state" name="state" type="text" placeholder="IL"
                    value={form.state} onChange={handleChange} onBlur={handleBlur}
                    maxLength={2}
                  />
                </Field>
                <Field id="zip" label="ZIP Code" required error={getError('zip')}>
                  <input
                    id="zip" name="zip" type="text" placeholder="60601"
                    value={form.zip} onChange={handleChange} onBlur={handleBlur}
                    maxLength={5}
                  />
                </Field>
              </div>

              <div className="sp-form-row">
                <Field id="insurance_company" label="Insurance Company" error="" className="sp-form-group--full">
                  <input
                    id="insurance_company" name="insurance_company" type="text"
                    placeholder="State Farm, Allstate, etc. (optional)"
                    value={form.insurance_company} onChange={handleChange}
                  />
                </Field>
              </div>

              <div className="sp-form-group sp-form-group--full">
                <label htmlFor="message">Additional Notes</label>
                <div className="sp-input-wrap">
                  <textarea
                    id="message" name="message" rows={4}
                    placeholder="Any concerns about your roof, prior damage, or questions you have…"
                    value={form.message} onChange={handleChange}
                  />
                </div>
              </div>

              {status === 'error' && (
                <p className="sp-form-error">
                  Something went wrong. Please try again or call us at{' '}
                  <a href="tel:+1XXXXXXXXXX">(XXX) XXX-XXXX</a>.
                </p>
              )}

              <button type="submit" className="sp-submit-btn" disabled={status === 'sending'}>
                {status === 'sending' ? 'Submitting…' : 'Schedule My Inspection →'}
              </button>

              <p className="sp-form-disclaimer">
                Free inspection. No obligation. We'll never share your information.
              </p>
            </form>
          )}
        </div>
      </section>

    </div>
  );
};

export default SignupPage;
